import { useEffect, useId, useRef, type ComponentProps, type ClipboardEvent, type KeyboardEvent, type ReactNode } from "react";

import { t } from "../contract/strings";
import { cx } from "./cx";

/**
 * Label and input as one thing, because the two are nested: the label text is the `<span>`
 * and the input is the label's own child, so tapping the text focuses the field without an
 * `htmlFor`/`id` pair to keep in step.
 */
export interface FieldProps extends Omit<ComponentProps<"input">, "className"> {
  label: ReactNode;
  fieldClassName?: string;
  inputClassName?: string;
  /**
   * Take the label out of the layout but not out of the accessibility tree -- a field
   * whose context is carried by the composition around it (a placeholder, a divider that
   * names the block) still needs to be named to a screen reader.
   */
  hideLabel?: boolean;
}

/** Mono caps: the instrument-label register, shared with panel headings and the divider. */
const LABEL =
  "block font-code text-label font-semibold uppercase tracking-label text-ink-soft mb-tight";

/**
 * `min-h-control` matches the buttons: 56px, the design's floor for a large touch target --
 * one-handed, at a table. The face and size are inherited from the base reset in `app.css`;
 * the app-wide input treatment supplies the borderless surface, rounded corners and focus
 * outline consistently, including for each box of the join code.
 */
const INPUT =
  "w-full py-3 px-input-x rounded-control border-0 " +
  "bg-surface-2 text-ink min-h-control placeholder:text-ink-soft placeholder:opacity-60";

export function Field({ label, fieldClassName, inputClassName, hideLabel, ...input }: FieldProps) {
  return (
    <label className={cx("block my-gutter", fieldClassName)}>
      {/* The nesting is the labelling: an sr-only span still names the input it wraps. */}
      <span className={cx(LABEL, hideLabel && "sr-only")}>{label}</span>
      <input className={cx(INPUT, inputClassName)} {...input} />
    </label>
  );
}

/**
 * What the join-code box accepts, ported verbatim from `app.js`.
 *
 * It admits every letter A-Z, including the four -- I, L, O, U -- that
 * `JOIN_CODE_ALPHABET` leaves out precisely because they are misread. That is deliberate
 * and is today's behaviour: filtering them here would silently rewrite what somebody typed,
 * and a code they read wrong should fail at the join with a message rather than become a
 * different code under their thumb.
 *
 * Exported on its own so it can be tested without a DOM, which is the only part of this
 * worth testing.
 */
export function filterJoinCode(value: string): string {
  return value.toUpperCase().replace(/[^0-9A-Z]/g, "");
}

/** Five characters, five boxes: the length of a session code and nothing else. */
const JOIN_CODE_BOXES = 5;

export interface CodeInputProps extends Omit<FieldProps, "onChange" | "value" | "inputClassName"> {
  value: string;
  onValueChange: (value: string) => void;
  /** Take the caret on mount, at the first empty slot -- an overlay opening is the intent. */
  focusOnMount?: boolean;
}

/**
 * One character, one outlined box, five of them.
 *
 * The design's session-code field is a row of boxes, and that is not only a look: read
 * aloud across a table, "three characters in, two to go" is information a single tracked
 * string hides. Each box is a real `<input>` -- five fake ones behind a hidden field would
 * break autofill, selection and the keyboard's own ideas about what is happening.
 *
 * Focus shifts by itself as the code fills, Backspace clears one character per press
 * -- a filled box loses its own character, an empty one sends the caret back and clears
 * the box it lands on, so repeated Backspace from the end empties the whole code --
 * arrows move between boxes, and a pasted code distributes itself from wherever focus
 * sits.
 * The value contract is unchanged: one uppercase alphanumeric string through the same
 * `filterJoinCode` as ever, so callers and tests cannot tell this apart from the single
 * box it replaced.
 */
export function CodeInput({
  value,
  onValueChange,
  fieldClassName,
  label,
  focusOnMount,
  ...rest
}: CodeInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  // A rendered page can hold two of these (the fields gallery and the join door both do),
  // so the group's label id cannot be a literal.
  const labelId = useId();

  useEffect(() => {
    if (focusOnMount) refs.current[Math.min(value.length, JOIN_CODE_BOXES - 1)]?.focus();
    // Mount intent only: the caret goes where the code already is, once.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- value.length is read at mount
  }, [focusOnMount]);

  // Controlled inputs re-render from the prop, but the DOM element survives, so moving
  // focus straight from the event handler needs no round-trip through an effect.
  const focusBox = (index: number) => {
    refs.current[Math.max(0, Math.min(JOIN_CODE_BOXES - 1, index))]?.focus();
  };

  /** Write `insertion` into slot `index`, shifting later characters right. */
  function insert(index: number, insertion: string): string {
    const clean = filterJoinCode(insertion);
    if (!clean) return value;
    const chars = value.split("");
    chars.splice(index, clean.length, ...clean.split(""));
    return chars.join("").slice(0, JOIN_CODE_BOXES);
  }

  function onChange(index: number, raw: string) {
    if (filterJoinCode(raw).length === 0) {
      // Emptied -- a character deleted, or selected text replaced with nothing: remove the
      // slot so the rest slides left, and stay put for whatever comes next.
      const chars = value.split("");
      chars.splice(index, 1);
      onValueChange(chars.join(""));
      return;
    }
    const typed = filterJoinCode(raw);
    onValueChange(insert(index, typed));
    // One character moves on to the next empty slot; a longer run -- a paste through
    // onChange, or a selection overwritten -- lands on the last slot that just filled.
    focusBox(index + (typed.length === 1 ? 1 : typed.length - 1));
  }

  function onKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      focusBox(index + 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusBox(index - 1);
    } else if (e.key === "Backspace") {
      // The Backspace is ours, not the browser's: one press clears one character.
      // A filled box loses its own character (the rest slide left) and the caret
      // stays for whatever comes next. An empty box has nothing of its own to
      // delete, so the caret moves back and the box it lands on loses its character
      // in the same press -- repeated Backspace from the end empties the whole code,
      // ending on the first box, which has nothing behind it.
      e.preventDefault();
      const chars = value.split("");
      if (e.currentTarget.value) {
        chars.splice(index, 1);
        onValueChange(chars.join(""));
      } else if (index > 0) {
        chars.splice(index - 1, 1);
        onValueChange(chars.join(""));
        focusBox(index - 1);
      }
    }
  }

  function onPaste(index: number, e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const text = e.clipboardData.getData("text");
    onValueChange(insert(index, text));
    // Wherever the paste ran out is where typing should carry on from.
    focusBox(index + Math.max(1, filterJoinCode(text).length));
  }

  // A box shows exactly its own character. No maxLength: a paste has to reach onChange
  // whole before it can be distributed.
  const BOX =
    "w-code-box h-control rounded-control border-0 bg-surface-2 text-center " +
    "font-code text-code-input font-bold uppercase text-ink disabled:opacity-45";

  return (
    <div className={cx("my-gutter", fieldClassName)}>
      <span className={LABEL} id={labelId}>
        {label}
      </span>
      <div className="flex gap-2" role="group" aria-labelledby={labelId}>
        {Array.from({ length: JOIN_CODE_BOXES }, (_, i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            className={BOX}
            type="text"
            autoCapitalize="characters"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            spellCheck={false}
            aria-label={t("ui.character_of", { n: i + 1, total: JOIN_CODE_BOXES })}
            value={value[i] ?? ""}
            onChange={(e) => onChange(i, e.target.value)}
            onKeyDown={(e) => onKeyDown(i, e)}
            onPaste={(e) => onPaste(i, e)}
            onFocus={(e) => e.currentTarget.select()}
            {...rest}
          />
        ))}
      </div>
    </div>
  );
}
